import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api-client';
import { useAuthStore } from '../../stores/authStore';
import type { LoginResponse, User } from '@spine-workspace/shared';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

// Shared light-theme input class (parchment card context)
const inputCls = `w-full bg-sand/40 border border-cream text-charcoal rounded-lg
  px-3 py-2 text-sm font-sans placeholder:text-stone
  hover:bg-sand/60 focus:outline-none focus:border-terracotta focus:shadow-focus-blue
  transition-all duration-normal`;

// Shared layout wrapper for all auth screens
function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center p-4 light-surface">
      <div className="w-full max-w-sm">
        {children}
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div className="text-center mb-10">
      <img src="/icon-512.png" alt="Spine Workspace" className="w-16 h-16 rounded-2xl mx-auto mb-5 shadow-whisper" />
      <h1 className="font-serif text-3xl font-medium text-charcoal" style={{ lineHeight: 1.1 }}>
        Spine Workspace
      </h1>
      <p className="font-sans text-sm text-stone mt-1.5">Eighth Era Studios</p>
    </div>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-ivory border border-cream rounded-xl shadow-whisper ${className}`}>
      {children}
    </div>
  );
}

export default function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [pendingVerification, setPendingVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const justVerified = searchParams.get('verified') === '1';
  const passwordChanged = searchParams.get('password_changed') === '1';
  const oauthError = searchParams.get('error');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        await api.post('/api/auth/register', { email, password, name });
        setPendingVerification(true);
      } else {
        const res = await api.post<LoginResponse>('/api/auth/login', { email, password });
        setAuth(res.user as User, res.token);
        navigate('/dashboard');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      if (msg === 'email_not_verified') {
        setPendingVerification(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email: forgotEmail });
    } catch {
      /* always show success */
    } finally {
      setForgotLoading(false);
      setForgotSent(true);
    }
  }

  async function handleResend() {
    setResendLoading(true);
    try {
      await api.post('/api/auth/resend-verification', { email });
      setResendSent(true);
    } catch {
      setResendSent(true);
    } finally {
      setResendLoading(false);
    }
  }

  // ── Forgot password screen ──────────────────────────────────────────
  if (forgotMode) {
    return (
      <AuthShell>
        <Logo />
        <Card className="p-6">
          <h2 className="font-serif text-xl font-medium text-charcoal mb-1" style={{ lineHeight: 1.2 }}>
            Forgot password?
          </h2>
          <p className="font-sans text-sm text-olive mb-5" style={{ lineHeight: 1.6 }}>
            Enter your email and we'll send a reset link if an account exists.
          </p>

          {forgotSent ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-full bg-terracotta-muted flex items-center justify-center mx-auto mb-4">
                <MailIcon />
              </div>
              <p className="font-sans text-sm font-medium text-charcoal mb-1">Check your email</p>
              <p className="font-sans text-sm text-olive" style={{ lineHeight: 1.6 }}>
                If an account exists for <span className="text-charcoal font-medium">{forgotEmail}</span>, a reset link has been sent.
              </p>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block font-sans text-xs font-medium text-olive mb-1.5">Email</label>
                <input className={inputCls} type="email" placeholder="you@eightherastudios.com"
                  value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required autoFocus />
              </div>
              <PrimaryButton loading={forgotLoading} label="Send reset link" />
            </form>
          )}

          <Divider />
          <BackLink onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); }} />
        </Card>
      </AuthShell>
    );
  }

  // ── Email verification pending screen ──────────────────────────────
  if (pendingVerification) {
    return (
      <AuthShell>
        <Logo />
        <Card className="p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-terracotta-muted flex items-center justify-center mx-auto mb-4">
            <MailIcon />
          </div>
          <h2 className="font-serif text-xl font-medium text-charcoal mb-2" style={{ lineHeight: 1.2 }}>
            Check your email
          </h2>
          <p className="font-sans text-sm text-olive mb-6" style={{ lineHeight: 1.6 }}>
            We sent a verification link to <span className="text-charcoal font-medium">{email}</span>. Click it to activate your account.
          </p>

          {resendSent ? (
            <p className="font-sans text-sm text-terracotta font-medium">Verification email resent.</p>
          ) : (
            <button onClick={handleResend} disabled={resendLoading}
              className="font-sans text-sm text-terracotta hover:underline disabled:opacity-50 font-medium">
              {resendLoading ? 'Sending…' : "Didn't receive it? Resend"}
            </button>
          )}

          <Divider />
          <BackLink onClick={() => { setPendingVerification(false); setMode('login'); }} />
        </Card>
      </AuthShell>
    );
  }

  // ── Main sign-in / register form ────────────────────────────────────
  return (
    <AuthShell>
      <Logo />

      <p className="font-sans text-xs text-stone text-center mb-4">
        Restricted to{' '}
        <span className="text-olive font-medium">@eightherastudios.com</span> and{' '}
        <span className="text-olive font-medium">@thenicegang.com</span>
      </p>

      <Card>
        {/* Banners */}
        {justVerified && <Banner type="success">Email verified! You can now sign in.</Banner>}
        {passwordChanged && <Banner type="success">Password updated! Sign in with your new password.</Banner>}
        {oauthError && !justVerified && <Banner type="error">{oauthErrorMessage(oauthError)}</Banner>}

        {/* Mode tabs */}
        <div className="flex border-b border-cream">
          {(['login', 'register'] as const).map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(''); }}
              className={`flex-1 py-3 text-sm font-sans font-medium transition-all duration-fast ${
                mode === m
                  ? 'text-terracotta border-b-2 border-terracotta -mb-px'
                  : 'text-stone hover:text-olive'
              }`}>
              {m === 'login' ? 'Sign in' : 'Register'}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block font-sans text-xs font-medium text-olive mb-1.5">Name</label>
                <input className={inputCls} type="text" placeholder="Your name"
                  value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
            )}

            <div>
              <label className="block font-sans text-xs font-medium text-olive mb-1.5">Email</label>
              <input className={inputCls} type="email" placeholder="you@eightherastudios.com"
                value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="font-sans text-xs font-medium text-olive">Password</label>
                {mode === 'login' && (
                  <button type="button" onClick={() => { setForgotMode(true); setForgotEmail(email); }}
                    className="font-sans text-[11px] text-terracotta hover:underline font-medium">
                    Forgot password?
                  </button>
                )}
              </div>
              <input className={inputCls} type="password"
                placeholder={mode === 'register' ? 'Min 8 characters' : '••••••••'}
                value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={mode === 'register' ? 8 : 1} />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 font-sans text-sm text-red-700">
                {error}
              </div>
            )}

            <PrimaryButton loading={loading} label={mode === 'login' ? 'Sign in' : 'Create account'} />
          </form>

          {/* OAuth divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-cream" />
            <span className="font-sans text-xs text-stone">or continue with</span>
            <div className="flex-1 h-px bg-cream" />
          </div>

          {/* OAuth buttons */}
          <div className="flex gap-2">
            {[
              { href: `${API_BASE}/api/auth/google`, icon: <GoogleIcon />, label: 'Google' },
              { href: `${API_BASE}/api/auth/github`, icon: <GitHubIcon />, label: 'GitHub' },
            ].map(({ href, icon, label }) => (
              <a key={label} href={href}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3
                           bg-sand/40 border border-cream rounded-lg
                           font-sans text-sm text-charcoal
                           hover:bg-sand hover:border-cream shadow-ring-warm
                           transition-all duration-fast">
                {icon}
                {label}
              </a>
            ))}
          </div>
        </div>
      </Card>
    </AuthShell>
  );
}

// ── Small shared sub-components ────────────────────────────────────────

function PrimaryButton({ loading, label }: { loading: boolean; label: string }) {
  return (
    <button type="submit" disabled={loading}
      className="w-full bg-terracotta text-ivory font-sans font-medium text-sm rounded-lg py-2.5
                 shadow-ring-terracotta hover:bg-terracotta-light
                 transition-all duration-normal disabled:opacity-50 disabled:cursor-not-allowed">
      {loading ? 'Please wait…' : label}
    </button>
  );
}

function Banner({ type, children }: { type: 'success' | 'error'; children: React.ReactNode }) {
  const cls = type === 'success'
    ? 'bg-green-50 border-green-200 text-green-800'
    : 'bg-red-50 border-red-200 text-red-700';
  return (
    <div className={`mx-6 mt-5 border rounded-lg px-3 py-2 font-sans text-sm ${cls}`}>
      {children}
    </div>
  );
}

function Divider() {
  return <div className="mt-5 pt-4 border-t border-cream" />;
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <div className="text-center -mt-1">
      <button onClick={onClick} className="font-sans text-xs text-stone hover:text-olive transition-colors">
        ← Back to sign in
      </button>
    </div>
  );
}

function MailIcon() {
  return (
    <svg className="w-6 h-6 text-terracotta" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  );
}

function oauthErrorMessage(code: string): string {
  const map: Record<string, string> = {
    oauth_denied: 'Sign-in was cancelled.',
    oauth_invalid_state: 'Invalid OAuth state. Please try again.',
    oauth_token_failed: 'Failed to complete sign-in. Please try again.',
    oauth_userinfo_failed: 'Could not fetch account info. Please try again.',
    oauth_no_email: 'Your GitHub account has no verified email. Please add one and retry.',
    oauth_domain_not_allowed: 'Access is restricted to @eightherastudios.com and @thenicegang.com accounts.',
    invalid_verify_token: 'Invalid verification link.',
    verify_token_expired: 'Verification link has expired. Please request a new one.',
  };
  return map[code] ?? 'Authentication failed. Please try again.';
}

function GoogleIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="text-charcoal">
      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
    </svg>
  );
}
